import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toPng, toSvg } from "html-to-image";
import { Download, Search, ZoomIn, ZoomOut } from "lucide-react";
import { fmtS } from "../../backbone.data";
import { resolveCloudProviderLogo } from "kontrolEdge/helpers/cloudProviderLogo";
import { Connector } from "../../backbone.types";
import { useTheme } from "../../../../../theme/components/ThemeProvider";
import { DataStateView } from "../StateViews";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import {
  connectorSearchHaystack,
  mapBbConnectorItemToTopologyConnector,
  sanitizeTopologyFileName,
  type BbConnectorListItem,
} from "../../requests/request";
import {
  connectedServiceTypeToGroup,
  connectorIsEstablishedToBgpStatusFilterId,
  matchesBgpStatusFilterSelection,
  matchesConnectorTypeGroupSelection,
  matchesVrfFilterSelection,
} from "../BackboneDetailsData";
import TopologyConnectorDetailPanel from "./TopologyConnectorDetailPanel";

/* ═══════════════════════════════════════════════════════════
   SVG canvas – radial topology (dual-theme: light + dark)
   Canvas dimensions are computed dynamically per-render based
   on node count; these are only the minimum defaults.
   ═══════════════════════════════════════════════════════════ */
const MIN_W = 900;
const MIN_H = 700;

/* ── Central hub ── */
const HUB_R = 56;

/* ── Zone accent colours (design-system semantic tokens — auto dark mode) ── */
const Z_CLOUD = { s: "var(--color-semantic-bg-warning-bold)", label: "CLOUD CONNECTIVITY" };

const Z_DC = { s: "var(--color-semantic-bg-brand-bold)", label: "PORT" };

/* ── Per-type accent for left-side service cards ── */
const TYPE_COLOR_SDHUB = "var(--color-semantic-fg-brand-alt)";
const TYPE_COLOR_FWAAS = "var(--color-semantic-fg-danger)";
const TYPE_COLOR_SDWAN = "var(--color-semantic-fg-warning)";

/* Status */
const C_OK = "#22c55e";
const C_DOWN = "var(--color-semantic-bg-danger-bold)";

/* Hub accent */
const HUB_ACCENT = "#06b6d4";

/* ── Node card dimensions ── */
const CARD_W = 190;
const CARD_H = 155;
const CARD_R = 10;
const ICON_R = 16;

/** Pan/zoom limits (Insight TopologyView-style interaction on SVG). */
const ZOOM_MIN = 0.35;
const ZOOM_MAX = 3;

/* providerColor is defined inside the component to support dark-mode-aware Azure */

/* ── DC / SD-WAN / Port icons (use semantic fg colour) ── */
const FG = "var(--color-semantic-fg-secondary)";

const iconServer = (
  <g>
    <rect
      x="-9"
      y="-7"
      width="18"
      height="6"
      rx="1.5"
      fill="none"
      stroke={FG}
      strokeWidth="1.2"
    />
    <rect
      x="-9"
      y="1"
      width="18"
      height="6"
      rx="1.5"
      fill="none"
      stroke={FG}
      strokeWidth="1.2"
    />
    <circle cx="5" cy="-4" r="1" fill={FG} />
    <circle cx="5" cy="4" r="1" fill={FG} />
  </g>
);

const iconSDWAN = (
  <g>
    <rect
      x="-8"
      y="-6"
      width="16"
      height="12"
      rx="2"
      fill="none"
      stroke={FG}
      strokeWidth="1.2"
    />
    <circle cx="0" cy="0" r="3" fill="none" stroke={FG} strokeWidth="1" />
    <circle cx="0" cy="0" r="1" fill={FG} />
  </g>
);

const iconFwaas = (
  <g>
    <path
      d="M0-8 L7 -3 L7 3 Q7 8 0 10 Q-7 8 -7 3 L-7-3 Z"
      fill="none"
      stroke={FG}
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path d="M-3 0 L-1 2 L3 -2" fill="none" stroke={FG} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

const iconSdwan = (
  <g>
    <circle cx="0" cy="0" r="7" fill="none" stroke={FG} strokeWidth="1.2" />
    <ellipse cx="0" cy="0" rx="3.5" ry="7" fill="none" stroke={FG} strokeWidth="0.8" />
    <line x1="-7" y1="0" x2="7" y2="0" stroke={FG} strokeWidth="0.8" />
    <line x1="-6" y1="-4" x2="6" y2="-4" stroke={FG} strokeWidth="0.6" />
    <line x1="-6" y1="4" x2="6" y2="4" stroke={FG} strokeWidth="0.6" />
  </g>
);

/* ── SVG text truncation ──
 * SVG <text> neither wraps nor clips, so long names overflow the card.
 * Budgets are derived from CARD_W minus padding at each font size; the
 * full value stays available in the card's <title> tooltip.
 */
const truncateSvgText = (text: string, maxChars: number) =>
  text.length > maxChars ? `${text.slice(0, maxChars - 1)}…` : text;

/** Usable chars for the 13px bold name row. */
const NAME_MAX_CHARS = 21;
/** Usable chars for the 10px monospace subtitle row. */
const SUBTITLE_MAX_CHARS = 26;

/* ── Bezier helper ── */
const bezier = (x1: number, y1: number, x2: number, y2: number, centerX: number, centerY: number) => {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = centerX - mx;
  const dy = centerY - my;
  const pull = 0.25;
  const cx1 = x1 + dx * pull;
  const cy1 = y1 + dy * pull;
  const cx2 = x2 + dx * pull;
  const cy2 = y2 + dy * pull;
  return `M${x1},${y1} C${cx1},${cy1} ${cx2},${cy2} ${x2},${y2}`;
};

/* ── Zone config type ── */
interface ZoneCfg {
  zone: typeof Z_CLOUD;
  items: Connector[];
  positions: { x: number; y: number }[];
}

/* ═══════════════════════════════════════════════════════════ */
export interface TopologyTabProps {
  /**
   * TEMPORARY: rows from `GET /kontroledge/bbaas/bb-connector` — used only to preview the topology layout until a dedicated topology API exists.
   */
  connectors?: BbConnectorListItem[];
  /** Global backbone filters (same AND logic as Connectors tab); applied before local topology pills. */
  vrfFilterSelection?: string[];
  connectorTypeFilterSelection?: string[];
  bgpStatusFilterSelection?: string[];
  /** Required for topology summary modal (BGP + routes APIs). */
  tenantId?: number | null;
  /** Backbone name displayed in the hub center. */
  backboneName?: string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}


export default function TopologyTab(props: TopologyTabProps) {
  const {
    connectors = [],
    vrfFilterSelection = [],
    connectorTypeFilterSelection = [],
    bgpStatusFilterSelection = [],
    tenantId = null,
    backboneName,
    loading = false,
    error = null,
    onRetry,
  } = props;
  const { theme } = useTheme();
  const isDark = theme === "theme-dark";

  /* ── Theme-aware SVG colours ── */
  const svgBg = "var(--color-semantic-bg-secondary)";
  const cardBg = isDark ? "#162032" : "#ffffff";
  const txtPri = isDark ? "#e2e8f0" : "#1e293b";
  const txtSec = isDark ? "#94a3b8" : "#64748b";
  const hubGlowOp = isDark ? 0.25 : 0.12;
  const hubStrokeOp = isDark ? 0.5 : 0.35;
  const linkActiveOp = isDark ? 1 : 0.85;
  const linkDownOp = isDark ? 0.4 : 0.3;
  const shadowOp = isDark ? 0.4 : 0.1;
  const iconCircleBg = isDark ? "#0c1222" : "#f1f5f9";

  /* ── Per-provider accent colours (semantic tokens + dark-mode-aware Azure) ── */
  const providerColor = useMemo<Record<string, string>>(() => ({
    AWS: "var(--color-semantic-bg-warning-bold)",
    Azure: isDark ? "#60a5fa" : "#2563eb",
    Oracle: "var(--color-semantic-bg-danger-bold)",
    GCP: "var(--color-semantic-bg-success-bold)",
  }), [isDark]);

  /** Same global scope as Connectors / summary KPIs; local topology pills apply on top. */
  const connectorsGloballyFiltered = useMemo(
    () =>
      connectors.filter((c) => {
        if (!matchesVrfFilterSelection(c.vrf?.displayName, vrfFilterSelection)) return false;
        const group = connectedServiceTypeToGroup(c.connectedServiceType);
        if (!matchesConnectorTypeGroupSelection(group, connectorTypeFilterSelection))
          return false;
        return matchesBgpStatusFilterSelection(
          connectorIsEstablishedToBgpStatusFilterId(c.bgpStatus?.isEstablished),
          bgpStatusFilterSelection
        );
      }),
    [
      connectors,
      vrfFilterSelection,
      connectorTypeFilterSelection,
      bgpStatusFilterSelection,
    ]
  );

  /**
   * TEMPORARY: drive the graph from BB-Connector list (same source as Connectors tab).
   * Replace with topology-specific DTO when backend provides it.
   */
  const allData: Connector[] = useMemo(
    () => connectorsGloballyFiltered.map(mapBbConnectorItemToTopologyConnector),
    [connectorsGloballyFiltered]
  );

  /* ── Topology local filter pills (temporarily disabled — use global Filters only) ──
  type TopoFilter = "all" | "up" | "down" | "port" | "cloud" | "sdwan" | "firewall";
  const [topoFilter, setTopoFilter] = useState<TopoFilter>("all");

  const data: Connector[] = useMemo(() => {
    switch (topoFilter) {
      case "up":
        return allData.filter((e) => e.bgp === "Established");
      case "down":
        return allData.filter((e) => e.bgp !== "Established");
      case "port":
        return allData.filter((e) => e.type === "Port");
      case "cloud":
        return allData.filter((e) => e.type === "Cloud");
      case "sdwan":
        return allData.filter((e) => e.type === "SDhub" || e.type === "Sdwan");
      case "firewall":
        return allData.filter((e) => e.type === "Fwaas");
      default:
        return allData;
    }
  }, [allData, topoFilter]);
  ───────────────────────────────────────────────────────────────────────────── */

  const data: Connector[] = allData;

  const topologyEmptyCopy = useMemo(() => {
    if (connectors.length === 0) {
      return {
        title: "No topology data yet",
        description:
          "This backbone has no connectors to display in the topology. Add connectors or try another view.",
      };
    }
    if (connectorsGloballyFiltered.length === 0) {
      return {
        title: "No connectors match global filters",
        description:
          "Adjust or clear the backbone Filters (VRF, connector type, BGP status) to see connectors in the topology.",
      };
    }
    return {
      title: "No topology data yet",
      description:
        "This backbone has no connectors to display in the topology. Add connectors or try another view.",
    };
  }, [connectors.length, connectorsGloballyFiltered.length, allData.length]);

  /* Local topology pill counts (disabled with pills UI — keep for easy restore)
  const filterCounts = useMemo(() => ({
    up: allData.filter((e) => e.bgp === "Established").length,
    down: allData.filter((e) => e.bgp !== "Established").length,
    port: allData.filter((e) => e.type === "Port").length,
    cloud: allData.filter((e) => e.type === "Cloud").length,
    sdwan: allData.filter((e) => e.type === "SDhub" || e.type === "Sdwan").length,
    firewall: allData.filter((e) => e.type === "Fwaas").length,
  }), [allData]);
  */

  const cloudEps = useMemo(() => data.filter((e) => e.type === "Cloud"), [data]);
  const sdhubEps = useMemo(() => data.filter((e) => e.type === "SDhub"), [data]);
  const fwaasEps = useMemo(() => data.filter((e) => e.type === "Fwaas"), [data]);
  const sdwanEps = useMemo(() => data.filter((e) => e.type === "Sdwan"), [data]);
  const portEps = useMemo(() => data.filter((e) => e.type === "Port"), [data]);


  const connectorRowById = useMemo(() => {
    const m = new Map<number, BbConnectorListItem>();
    for (const c of connectorsGloballyFiltered) m.set(c.id, c);
    return m;
  }, [connectorsGloballyFiltered]);

  /* ── Measure container width so canvas fills the available space ── */
  const viewportRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ── Layout: radial ring around the hub ──
   * Every connector sits on a circle centered on the hub. Adjacent cards
   * alternate between an inner and an outer ring (staggered) so labels and
   * cards never collide even with many connectors. The base radius grows
   * with the node count so the angular chord always fits a card.
   */
  const RING_STAGGER = 95;

  const layout = useMemo(() => {
    const n = Math.max(data.length, 1);
    // Chord between adjacent centers ≈ 2R·sin(π/n); staggering lets cards
    // tolerate ~0.72·CARD_W chords without visual overlap.
    const baseR = Math.max(
      340,
      (n * CARD_W * 0.72) / (2 * Math.PI) + HUB_R + 220
    );
    const outerR = baseR + RING_STAGGER;
    const margin = 140;
    const canvasW = Math.max(
      MIN_W,
      containerWidth,
      (outerR + CARD_W / 2 + margin) * 2
    );
    const canvasH = Math.max(MIN_H, (outerR + CARD_H / 2 + margin) * 2);

    return { W: canvasW, H: canvasH, CX: canvasW / 2, CY: canvasH / 2, baseR };
  }, [data.length, containerWidth]);

  const { W, H: canvasH, CX, CY, baseR } = layout;

  /* ── Compute node positions: type groups laid out as contiguous arcs ──
   * Groups keep their own accent/label; items are distributed uniformly
   * over the full circle (starting at 12 o'clock), alternating between the
   * inner and outer ring.
   */
  const zones: ZoneCfg[] = useMemo(() => {
    const groups = [
      { zone: Z_CLOUD, items: cloudEps },
      { zone: Z_DC, items: portEps },
      { zone: { s: TYPE_COLOR_SDHUB, label: "SDWAN HUB" }, items: sdhubEps },
      { zone: { s: TYPE_COLOR_FWAAS, label: "FWAAS" }, items: fwaasEps },
      { zone: { s: TYPE_COLOR_SDWAN, label: "SDWAN" }, items: sdwanEps },
    ].filter((g) => g.items.length > 0);

    const total = groups.reduce((sum, g) => sum + g.items.length, 0);
    if (total === 0) return [];

    const angleStep = (2 * Math.PI) / total;
    const startAngle = -Math.PI / 2; // first card at 12 o'clock

    let flatIndex = 0;
    return groups.map((g) => {
      const angles = g.items.map((_, i) => startAngle + (flatIndex + i) * angleStep);
      const positions = g.items.map((_, i) => {
        const r = (flatIndex + i) % 2 === 0 ? baseR : baseR + RING_STAGGER;
        return {
          x: CX + Math.cos(angles[i]) * r,
          y: CY + Math.sin(angles[i]) * r,
        };
      });

      flatIndex += g.items.length;
      return { zone: g.zone, items: g.items, positions };
    });
  }, [cloudEps, sdhubEps, fwaasEps, sdwanEps, portEps, CX, CY, baseR]);

  /** Map topology node id → SVG coordinates (for search focus & zoom). */
  const positionByEpId = useMemo(() => {
    const m = new Map<string, { x: number; y: number }>();
    for (const z of zones) {
      z.items.forEach((ep, i) => {
        const p = z.positions[i];
        if (p) m.set(ep.id, p);
      });
    }
    return m;
  }, [zones]);

  const exportRootRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef(pan);
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ sx: 0, sy: 0, px: 0, py: 0 });
  const dragActiveRef = useRef(false);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [focusedConnectorId, setFocusedConnectorId] = useState<number | null>(
    null
  );
  const [selectedConnector, setSelectedConnector] =
    useState<BbConnectorListItem | null>(null);

  const fitToView = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    const scaleX = cw / W;
    const scaleY = ch / canvasH;
    const fitZoom = Math.max(ZOOM_MIN, Math.min(1, Math.min(scaleX, scaleY) * 0.95));
    setZoom(fitZoom);
    setPan({
      x: (cw - W * fitZoom) / 2,
      y: (ch - canvasH * fitZoom) / 2,
    });
  }, [W, canvasH]);

  useLayoutEffect(() => {
    fitToView();
  }, [fitToView, data.length]);

  /** Wheel zoom toward cursor (same interaction model as canvas tools in Insight TopologyView). */
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = e.deltaY > 0 ? 0.92 : 1.08;
      setZoom((z0) => {
        const z1 = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z0 * factor));
        const k = z1 / z0;
        setPan((p) => ({
          x: mx - k * (mx - p.x),
          y: my - k * (my - p.y),
        }));
        return z1;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const searchQ = searchQuery.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (searchQ.length === 0) return [];
    return connectorsGloballyFiltered.filter((c) =>
      connectorSearchHaystack(c).includes(searchQ)
    );
  }, [connectorsGloballyFiltered, searchQ]);

  const focusConnectorInView = useCallback(
    (numericId: number) => {
      const row = connectorRowById.get(numericId);
      if (!row) return;
      const ep = data.find((d) => d.bbaasConnectorId === numericId);
      if (!ep) return;
      const pos = positionByEpId.get(ep.id);
      if (!pos) return;
      const el = viewportRef.current;
      if (!el) return;
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      const z = Math.min(2, ZOOM_MAX);
      setZoom(z);
      setPan({
        x: cw / 2 - pos.x * z,
        y: ch / 2 - pos.y * z,
      });
      setFocusedConnectorId(numericId);
    },
    [connectorRowById, data, positionByEpId]
  );

  const exportDiagram = useCallback(
    async (format: "svg" | "png") => {
      const node = exportRootRef.current;
      if (!node || data.length === 0) return;
      try {
        setExporting(true);
        const commonOpts = {
          cacheBust: true,
          backgroundColor: svgBg,
          width: W,
          height: canvasH,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore html-to-image supports skipFonts
          skipFonts: true,
        } as const;
        const stamp = sanitizeTopologyFileName(new Date().toISOString());
        if (format === "svg") {
          const dataUrl = await toSvg(node, commonOpts);
          const svgText = await (await fetch(dataUrl)).text();
          const blob = new Blob([svgText], { type: "image/svg+xml" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `backbone-topology-${stamp}.svg`;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          const dataUrl = await toPng(node, { ...commonOpts, pixelRatio: 2 });
          const blob = await (await fetch(dataUrl)).blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `backbone-topology-${stamp}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error("Topology export failed", err);
      } finally {
        setExporting(false);
      }
    },
    [data.length, svgBg]
  );

  const onViewportPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const t = e.target;
    if (t instanceof Element && t.closest("[data-topology-connector]")) {
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    dragActiveRef.current = true;
    dragRef.current = {
      sx: e.clientX,
      sy: e.clientY,
      px: panRef.current.x,
      py: panRef.current.y,
    };
    setDragging(true);
  };

  const onViewportPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragActiveRef.current) return;
    setPan({
      x: dragRef.current.px + (e.clientX - dragRef.current.sx),
      y: dragRef.current.py + (e.clientY - dragRef.current.sy),
    });
  };

  const endViewportPan = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragActiveRef.current) return;
    dragActiveRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    setDragging(false);
  };

  const searchActive = searchQ.length > 0;

  /* ── Render a single node card ── */
  const renderCard = (
    ep: Connector,
    pos: { x: number; y: number },
    zoneColor: string,
    opts: {
      row?: BbConnectorListItem;
      searchMatch: boolean;
      searchFocused: boolean;
    }
  ) => {
    const isUp = ep.bgp === "Established";
    const cx = pos.x;
    const cy = pos.y;
    const left = cx - CARD_W / 2;
    const top = cy - CARD_H / 2;
    const pad = 14;

    const isCloud = ep.type === "Cloud" && ep.provider;
    const logoUrl =
      isCloud && ep.provider ? resolveCloudProviderLogo(ep.provider) ?? undefined : undefined;
    let icon: React.ReactNode = iconServer;
    if (ep.type === "SDhub") icon = iconSDWAN;
    else if (ep.type === "Fwaas") icon = iconFwaas;
    else if (ep.type === "Sdwan") icon = iconSdwan;

    const label = ep.type === "Cloud" && ep.provider ? ep.provider : ep.label;
    const subtitle =
      ep.type === "Cloud"
        ? ep.cloudRegion || ep.loc || "—"
        : ep.type === "SDhub"
        ? `${ep.label !== label ? ep.label : "SDWAN Hub"}`
        : ep.loc || "—";
    const typeLabel =
      ep.type === "Cloud"
        ? "Cloud Connectivity"
        : ep.type === "SDhub"
        ? "SDWAN Hub"
        : ep.type === "Fwaas"
        ? "FWaaS"
        : ep.type === "Sdwan"
        ? "SDWAN"
        : "Port";
    const throughputStr = ep.bw > 0 ? fmtS(ep.bw) : "—";
    const statusLabel = isUp ? "Active" : "Down";
    const statusColor = isUp ? C_OK : C_DOWN;
    const statusBg = isUp
      ? isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)"
      : isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.1)";

    const tooltip = isCloud
      ? [
          ep.provider,
          ep.cloudRegion ? `Region: ${ep.cloudRegion}` : "",
          ep.cloudAccount ? `Account: ${ep.cloudAccount}` : "",
          ep.cloudVPC ? `VPC: ${ep.cloudVPC}` : "",
        ]
          .filter(Boolean)
          .join("\n")
      : `${ep.label}\n${ep.loc}`;

    const dimmed = searchActive && !opts.searchMatch;

    const detailRowY = top + 108;
    const dividerY = top + 100;

    return (
      <g
        key={ep.id}
        data-topology-connector
        opacity={dimmed ? 0.22 : 1}
        className="transition-opacity duration-200"
        style={{ cursor: opts.row ? "pointer" : "default" }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (opts.row) setSelectedConnector(opts.row);
        }}
      >
        <title>{tooltip}</title>
        {opts.searchFocused && (
          <rect
            x={left - 4}
            y={top - 4}
            width={CARD_W + 8}
            height={CARD_H + 8}
            rx={CARD_R + 4}
            fill="none"
            stroke="var(--color-semantic-border-brand-primary, #3b82f6)"
            strokeWidth={2}
            pointerEvents="none"
          />
        )}

        {/* Card background — neutral border like GenericCard */}
        <rect
          x={left}
          y={top}
          width={CARD_W}
          height={CARD_H}
          rx={CARD_R}
          fill={cardBg}
          stroke={isDark ? "#334155" : "#e2e8f0"}
          strokeWidth={1}
          filter="url(#cardShadow)"
        />
        {/* Top accent stripe for type colour */}
        <rect
          x={left + CARD_R}
          y={top}
          width={CARD_W - CARD_R * 2}
          height={3}
          fill={zoneColor}
        />

        {/* Icon circle */}
        <circle
          cx={left + pad + ICON_R}
          cy={top + pad + ICON_R - 2}
          r={ICON_R}
          fill={iconCircleBg}
          stroke={isDark ? "#334155" : "#e2e8f0"}
          strokeWidth={1}
        />
        {logoUrl ? (
          <image
            href={logoUrl}
            x={left + pad + ICON_R - 11}
            y={top + pad + ICON_R - 13}
            width={22}
            height={22}
          />
        ) : (
          <g transform={`translate(${left + pad + ICON_R}, ${top + pad + ICON_R - 2}) scale(0.8)`}>
            {icon}
          </g>
        )}

        {/* Status tag (top-right) */}
        <rect
          x={left + CARD_W - pad - 58}
          y={top + pad - 2}
          width={58}
          height={20}
          rx={10}
          fill={statusBg}
        />
        <circle
          cx={left + CARD_W - pad - 58 + 12}
          cy={top + pad + 8}
          r={3}
          fill={statusColor}
        />
        <text
          x={left + CARD_W - pad - 58 + 22}
          y={top + pad + 12}
          fontSize="9"
          fontWeight="600"
          fill={statusColor}
        >
          {statusLabel}
        </text>

        {/* Name (truncated — full value in the tooltip) */}
        <text
          x={left + pad}
          y={top + 66}
          fontSize="13"
          fontWeight="700"
          fill={txtPri}
        >
          {truncateSvgText(label, NAME_MAX_CHARS)}
        </text>

        {/* Subtitle (truncated — full value in the tooltip) */}
        <text
          x={left + pad}
          y={top + 82}
          fontSize="10"
          fill={txtSec}
          fontFamily="monospace"
        >
          {truncateSvgText(subtitle, SUBTITLE_MAX_CHARS)}
        </text>

        {/* Divider */}
        <line
          x1={left + pad}
          y1={dividerY}
          x2={left + CARD_W - pad}
          y2={dividerY}
          stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}
          strokeWidth={1}
        />

        {/* Detail rows */}
        <text x={left + pad} y={detailRowY + 10} fontSize="9" fill={txtSec}>
          Throughput
        </text>
        <text
          x={left + CARD_W - pad}
          y={detailRowY + 10}
          textAnchor="end"
          fontSize="9"
          fontWeight="600"
          fill={txtPri}
        >
          {throughputStr}
        </text>

        <text x={left + pad} y={detailRowY + 26} fontSize="9" fill={txtSec}>
          Type
        </text>
        <text
          x={left + CARD_W - pad}
          y={detailRowY + 26}
          textAnchor="end"
          fontSize="9"
          fontWeight="600"
          fill={txtPri}
        >
          {typeLabel}
        </text>

        {/* Tags row removed (it rendered as a stray "–"); tags live in the
            connector detail panel. Keeps the card free of dead space. */}
      </g>
    );
  };

  return (
    <DataStateView
      loading={loading}
      error={error}
      isEmpty={data.length === 0}
      onRetry={onRetry}
      loadingContent={
        <div className="mt-4 space-y-4">
          <SkeletonBlock h={120} className="rounded-md w-full" />
          <SkeletonBlock h={420} className="rounded-md w-full" />
          <SkeletonBlock h={56} className="rounded-md w-full" />
        </div>
      }
      errorTitle="Failed to load topology"
      emptyTitle={topologyEmptyCopy.title}
      emptyDescription={topologyEmptyCopy.description}
    >
      <>
        {/* ── Topology Diagram (pan/zoom + search + export — pattern from Insight TopologyView) ── */}
        <div className="mt-1 rounded-md border border-gray-300 bg-semantic-bg-primary overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-gray-300 px-4 py-3 sm:flex-row sm:items-center sm:justify-end">
            {/* Local topology filter pills — commented out while testing global Filters only.
            <div className="flex items-center gap-1.5 flex-wrap">
              {([
                { key: "all" as TopoFilter, label: "View All" },
                { key: "up" as TopoFilter, label: "Up" },
                { key: "down" as TopoFilter, label: "Down" },
                { key: "port" as TopoFilter, label: "Port" },
                { key: "cloud" as TopoFilter, label: "Cloud" },
                { key: "sdwan" as TopoFilter, label: "SDWAN" },
                { key: "firewall" as TopoFilter, label: "Firewall" },
              ] as const).map((f) => {
                const count = f.key === "all" ? allData.length : filterCounts[f.key];
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setTopoFilter(f.key)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer transition-colors ${
                      topoFilter === f.key
                        ? "bg-semantic-bg-brand-bold-secondary text-white"
                        : "bg-semantic-bg-primary text-semantic-text-secondary border border-semantic-border-primary hover:bg-semantic-state-active"
                    }`}
                  >
                    {f.label}
                    <span className="ml-1 opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
            */}
            <div className="flex w-full items-center gap-2 sm:w-auto sm:flex-shrink-0">
              <div className="relative min-w-[160px] sm:max-w-[200px]">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-semantic-text-secondary" />
                <input
                  ref={searchInputRef}
                  type="search"
                  placeholder="Search connectors…"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchOpen(true);
                    setFocusedConnectorId(null);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  onBlur={() =>
                    setTimeout(() => setSearchOpen(false), 180)
                  }
                  className="w-full rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary py-2 pl-9 pr-3 text-sm text-semantic-text-primary placeholder:text-semantic-text-secondary focus:border-semantic-outline-primary focus:outline-none focus:ring-2 focus:ring-semantic-bg-brand-secondary"
                  title="Search topology (⌘K / Ctrl+K)"
                />
                {searchOpen && searchResults.length > 0 && searchQ.length > 0 && (
                  <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-auto rounded-lg border border-semantic-border-primary bg-semantic-bg-primary py-1 shadow-lg">
                    {searchResults.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm text-semantic-text-primary hover:bg-semantic-bg-secondary"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSearchQuery(c.name || String(c.id));
                            focusConnectorInView(c.id);
                            setSearchOpen(false);
                          }}
                        >
                          <span className="font-medium">{c.name}</span>
                          <span className="ml-2 text-xs text-semantic-text-secondary">
                            {c.connectedServiceType}
                            {c.vrf?.displayName
                              ? ` · ${c.vrf.displayName}`
                              : ""}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex items-center gap-1 rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary p-0.5">
                <button
                  type="button"
                  className="rounded p-1.5 text-semantic-text-secondary hover:bg-semantic-bg-tertiary hover:text-semantic-text-primary"
                  title="Zoom out"
                  onClick={() =>
                    setZoom((z) =>
                      Math.max(ZOOM_MIN, z * 0.88)
                    )
                  }
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="rounded p-1.5 text-semantic-text-secondary hover:bg-semantic-bg-tertiary hover:text-semantic-text-primary"
                  title="Zoom in"
                  onClick={() =>
                    setZoom((z) =>
                      Math.min(ZOOM_MAX, z * 1.12)
                    )
                  }
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="px-2 py-1 text-xs font-medium text-semantic-text-secondary hover:text-semantic-text-primary"
                  title="Reset pan & zoom"
                  onClick={() => {
                    fitToView();
                  }}
                >
                  Reset
                </button>
              </div>
              <button
                type="button"
                disabled={exporting || data.length === 0}
                onClick={() => void exportDiagram("svg")}
                className="flex items-center gap-1 rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary px-2 py-1.5 text-xs font-semibold text-semantic-text-primary shadow-sm hover:bg-semantic-bg-tertiary disabled:cursor-not-allowed disabled:opacity-50"
                title="Export as SVG"
              >
                <Download className="h-3.5 w-3.5" />
                SVG
              </button>
              <button
                type="button"
                disabled={exporting || data.length === 0}
                onClick={() => void exportDiagram("png")}
                className="flex items-center gap-1 rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary px-2 py-1.5 text-xs font-semibold text-semantic-text-primary shadow-sm hover:bg-semantic-bg-tertiary disabled:cursor-not-allowed disabled:opacity-50"
                title="Export as PNG"
              >
                <Download className="h-3.5 w-3.5" />
                PNG
              </button>
            </div>
          </div>
          <p className="px-4 pb-2 pt-1 text-xs text-semantic-text-secondary">
            Scroll to zoom · drag background to pan · click a connector for
            details
          </p>
          <div className="flex" style={{ height: `clamp(500px, ${canvasH}px, calc(100vh - 220px))` }}>
          <div
            ref={viewportRef}
            className={`relative flex-1 min-w-0 mb-4 touch-none select-none overflow-hidden border border-semantic-border-primary bg-semantic-bg-secondary ${
              dragging ? "cursor-grabbing" : "cursor-grab"
            }`}
            style={{ height: "100%", touchAction: "none" }}
            onPointerDown={onViewportPointerDown}
            onPointerMove={onViewportPointerMove}
            onPointerUp={endViewportPan}
            onPointerCancel={endViewportPan}
            onLostPointerCapture={() => {
              dragActiveRef.current = false;
              setDragging(false);
            }}
          >
            <div
              ref={exportRootRef}
              className="will-change-transform"
              style={{
                width: W,
                height: canvasH,
                backgroundColor: svgBg,
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: "0 0",
              }}
            >
            <svg
              width={W}
              height={canvasH}
              viewBox={`0 0 ${W} ${canvasH}`}
            >
              <defs>
                {/* ── Card shadow ── */}
                <filter
                  id="cardShadow"
                  x="-20%"
                  y="-20%"
                  width="140%"
                  height="140%"
                >
                  <feDropShadow
                    dx="0"
                    dy="2"
                    stdDeviation="6"
                    floodColor="#000"
                    floodOpacity={shadowOp}
                  />
                </filter>
                {/* ── Hub glow ── */}
                <filter
                  id="hubGlow"
                  x="-50%"
                  y="-50%"
                  width="200%"
                  height="200%"
                >
                  <feGaussianBlur stdDeviation="18" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                {/* ── Hub radial gradient ── */}
                <radialGradient id="hubRadial" cx="50%" cy="50%" r="50%">
                  <stop
                    offset="0%"
                    stopColor={HUB_ACCENT}
                    stopOpacity={hubGlowOp}
                  />
                  <stop
                    offset="60%"
                    stopColor={HUB_ACCENT}
                    stopOpacity={hubGlowOp * 0.3}
                  />
                  <stop offset="100%" stopColor={HUB_ACCENT} stopOpacity={0} />
                </radialGradient>
                {/* ── Animated dash ── */}
                <style>{`
                                .topo-link-active {
                                    stroke-dasharray: 14 5;
                                    animation: topoDash 1.8s linear infinite;
                                }
                                @keyframes topoDash {
                                    to { stroke-dashoffset: -19; }
                                }
                                .topo-link-down {
                                    stroke-dasharray: 6 5;
                                }
                            `}</style>
              </defs>

              {/* ── Canvas background ── */}
              <rect x="0" y="0" width={W} height={canvasH} rx="12" fill={svgBg} />

              {/* Zone labels removed — the accent stripe / provider logo on
                  each card plus the bottom legend already identify types. */}

              {/* ── Connection lines with bandwidth labels ── */}
              {zones.map((z) =>
                z.items.map((ep, i) => {
                  const pos = z.positions[i];
                  if (!pos) return null;
                  const isUp = ep.bgp === "Established";
                  const linkColor =
                    ep.type === "Cloud" && ep.provider && providerColor[ep.provider]
                      ? providerColor[ep.provider]
                      : ep.type === "Fwaas" ? TYPE_COLOR_FWAAS
                      : ep.type === "Sdwan" ? TYPE_COLOR_SDWAN
                      : ep.type === "SDhub" ? TYPE_COLOR_SDHUB
                      : z.zone.s;
                  const hasTraffic = isUp && ep.bw > 0;
                  const bwLabel = isUp
                    ? ep.bw > 0 ? fmtS(ep.bw) : "0 Mbps"
                    : `DOWN · 0 Mbps`;
                  const mx = (pos.x + CX) / 2;
                  const my = (pos.y + CY) / 2;
                  const labelW = bwLabel.length * 5.5 + 16;
                  return (
                    <g key={`link-${ep.id}`} style={{ pointerEvents: "none" }}>
                      <path
                        d={bezier(pos.x, pos.y, CX, CY, CX, CY)}
                        fill="none"
                        stroke={isUp ? linkColor : C_DOWN}
                        strokeWidth={2.5}
                        opacity={hasTraffic ? linkActiveOp : linkDownOp}
                        className={hasTraffic ? "topo-link-active" : "topo-link-down"}
                      />
                      <rect
                        x={mx - labelW / 2} y={my - 10}
                        width={labelW} height={20} rx={10}
                        fill={svgBg}
                        stroke={isUp ? linkColor : C_DOWN}
                        strokeWidth={0.8} strokeOpacity={0.5}
                      />
                      <text
                        x={mx} y={my + 4}
                        textAnchor="middle" fontSize="8" fontWeight="600"
                        fill={isUp ? linkColor : txtSec}
                      >
                        {bwLabel}
                      </text>
                    </g>
                  );
                })
              )}

              {/* ── Central Hub (non-interactive for pan — hits pass to canvas) ── */}
              <g style={{ pointerEvents: "none" }}>
              <circle cx={CX} cy={CY} r={HUB_R + 36} fill="url(#hubRadial)" />
              {/* Outer ring */}
              <circle
                cx={CX}
                cy={CY}
                r={HUB_R + 18}
                fill="none"
                stroke={HUB_ACCENT}
                strokeWidth={0.6}
                strokeOpacity={isDark ? 0.15 : 0.1}
              />
              {/* Main circle */}
              <circle
                cx={CX}
                cy={CY}
                r={HUB_R}
                fill={svgBg}
                stroke={HUB_ACCENT}
                strokeWidth={1.8}
                strokeOpacity={hubStrokeOp}
                filter="url(#hubGlow)"
              />
              {/* Inner ring */}
              <circle
                cx={CX}
                cy={CY}
                r={HUB_R - 12}
                fill="none"
                stroke={HUB_ACCENT}
                strokeWidth={0.8}
                strokeOpacity={isDark ? 0.25 : 0.15}
              />

              {/* Hub icon — interconnected nodes */}
              <g
                transform={`translate(${CX}, ${CY})`}
                opacity={isDark ? 0.55 : 0.4}
              >
                <circle r={4} fill={HUB_ACCENT} />
                {[0, 72, 144, 216, 288].map((angle) => {
                  const rad = (angle * Math.PI) / 180;
                  const ox = Math.cos(rad) * 20;
                  const oy = Math.sin(rad) * 20;
                  return (
                    <g key={angle}>
                      <line
                        x1={0}
                        y1={0}
                        x2={ox}
                        y2={oy}
                        stroke={HUB_ACCENT}
                        strokeWidth={0.8}
                        opacity={0.4}
                      />
                      <circle
                        cx={ox}
                        cy={oy}
                        r={3}
                        fill={HUB_ACCENT}
                        opacity={0.7}
                      />
                    </g>
                  );
                })}
              </g>

              {/* Hub text */}
              <text
                x={CX}
                y={CY + HUB_R + 20}
                textAnchor="middle"
                fontSize="14"
                fontWeight="800"
                fill={txtPri}
                letterSpacing="0.5"
              >
                {backboneName || "L3VPN Backbone"}
              </text>
              <text
                x={CX}
                y={CY + HUB_R + 34}
                textAnchor="middle"
                fontSize="9"
                fill={txtSec}
              >
                Backbone as a Service
              </text>
              </g>

              {/* ── Node cards ── */}
              {zones.map((z) =>
                z.items.map((ep, i) => {
                  const pos = z.positions[i];
                  if (!pos) return null;
                  const row =
                    ep.bbaasConnectorId != null
                      ? connectorRowById.get(ep.bbaasConnectorId)
                      : undefined;
                  const hay =
                    row != null
                      ? connectorSearchHaystack(row)
                      : `${ep.label} ${ep.vrf}`.toLowerCase();
                  const searchMatch =
                    !searchActive || hay.includes(searchQ);
                  const searchFocused =
                    focusedConnectorId != null &&
                    ep.bbaasConnectorId === focusedConnectorId;
                  const cardColor =
                    ep.type === "Cloud" && ep.provider && providerColor[ep.provider]
                      ? providerColor[ep.provider]
                      : ep.type === "Fwaas" ? TYPE_COLOR_FWAAS
                      : ep.type === "Sdwan" ? TYPE_COLOR_SDWAN
                      : ep.type === "SDhub" ? TYPE_COLOR_SDHUB
                      : z.zone.s;
                  return renderCard(ep, pos, cardColor, {
                    row,
                    searchMatch,
                    searchFocused,
                  });
                })
              )}

            </svg>
            </div>
          </div>

          {selectedConnector && (
            <TopologyConnectorDetailPanel
              connector={selectedConnector}
              onClose={() => setSelectedConnector(null)}
              tenantId={tenantId}
            />
          )}
          </div>
        </div>

        {/* ── Legend ── */}
        <div className="mt-4 rounded-md border border-gray-300 bg-semantic-bg-primary">
          <div className="py-3 px-6 flex flex-wrap gap-6 items-center">
            <span className="text-xs font-medium text-semantic-text-secondary">
              Legend:
            </span>
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: C_OK }}
              />
              <span className="text-xs text-semantic-text-secondary">
                Connected Established
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: C_DOWN }}
              />
              <span className="text-xs text-semantic-text-secondary">
                Disconnected
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block w-6 h-0"
                style={{
                  borderTop: `2px dashed ${Z_CLOUD.s}`,
                }}
              />
              <span className="text-xs text-semantic-text-secondary">
                Active link
              </span>
            </div>
            {[
              { label: "AWS", color: providerColor.AWS },
              { label: "Azure", color: providerColor.Azure },
              { label: "Oracle", color: providerColor.Oracle },
              { label: "GCP", color: providerColor.GCP },
              { label: "SDWAN Hub", color: TYPE_COLOR_SDHUB },
              { label: "FWaaS", color: TYPE_COLOR_FWAAS },
              { label: "SDWAN", color: TYPE_COLOR_SDWAN },
              { label: "Port", color: Z_DC.s },
            ].map((z) => (
              <div key={z.label} className="flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{
                    border: `1.5px solid ${z.color}`,
                    background: z.color,
                    opacity: 0.3,
                  }}
                />
                <span className="text-xs text-semantic-text-secondary">
                  {z.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </>
    </DataStateView>
  );
}
